import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HistoryEstimate } from '@1po/1po-bff-fe-spec/generated/estimate/response/GetEstimateHistory';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { useIsB2B } from 'components/B2BComponents/useIsB2B';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { isDelegationSessionStatusLoading } from 'domains/delegation/DelegationSession.store';
import { EstimateIdParam } from 'domains/estimate/Estimate.types';
import { getTradingProfile, getUserRights, UserRole } from 'domains/user';
import { InfiniteScrollable } from 'UI';
import { getData, hasData, hasUserAnyRight, LOADING, NOT_FOUND } from 'utils';
import {
  createNewEstimate,
  fetchEstimateByIdRequest,
  fetchEstimateHistoryRequest,
  fetchSettingsRequest,
  getCurrentEstimateId,
  getEstimateById,
  getEstimateHistory,
  getEstimateHistoryHasMore,
  getEstimateHistorySearchStatus,
  getEstimateSettings,
  getEstimateVehicleKey,
} from './Estimate.store';

export function useFetchEstimateSettingsSubscription() {
  const dispatch = useDispatch();
  const settings = hasData(useSelector(getEstimateSettings));
  const garageId = useSelector(getTradingProfile)?.buyerId;
  const userRights = getData(useSelector(getUserRights));
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (!hasUserAnyRight([UserRole.COMMAND, UserRole.CONNECT_COMMANDE], userRights)) {
      return;
    }
    if (!settings && garageId) {
      dispatch(fetchSettingsRequest());
    }
  }, [dispatch, settings, garageId, userRights, isDelegationLoading]);
}

export const useFetchEstimate = (estimateId: string) => {
  const dispatch = useDispatch();
  const estimate = useSelector((state: RootState) => getEstimateById(state, estimateId));
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (estimate === undefined) {
      dispatch(fetchEstimateByIdRequest(estimateId));
    }
  }, [dispatch, estimate, estimateId, isDelegationLoading]);
};

export const useFetchEstimateHistory = (): InfiniteScrollable<HistoryEstimate> => {
  const dispatch = useDispatch();
  const estimateHistory = useSelector(getEstimateHistory);
  const hasMore = useSelector(getEstimateHistoryHasMore);
  const searchStatus = useSelector(getEstimateHistorySearchStatus);
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  function onLoadMore() {
    if (isDelegationLoading) {
      return;
    }
    if (searchStatus === NOT_FOUND) {
      return;
    }
    if (searchStatus !== LOADING) {
      dispatch(fetchEstimateHistoryRequest());
    }
  }

  return { loadMore: onLoadMore, hasMore, currentData: estimateHistory };
};

export function useCreateEstimateOnCatalogVehicleChange() {
  const dispatch = useDispatch();
  const estimateId = useSelector(getCurrentEstimateId);
  const catalogVehicleDetail = useSelector(getLastVehicleDetail);
  const catalogVehicleKey = catalogVehicleDetail?.vehicleKey;
  const estimateVehicleKey = useSelector((state: RootState) => getEstimateVehicleKey(state, estimateId));
  const userRights = getData(useSelector(getUserRights));
  const isB2B = useIsB2B();
  const isDelegationLoading = useSelector(isDelegationSessionStatusLoading);

  useEffect(() => {
    if (isDelegationLoading) {
      return;
    }
    if (isB2B) {
      return;
    }
    if (!hasUserAnyRight([UserRole.COMMAND, UserRole.CONNECT_COMMANDE], userRights)) {
      return;
    }

    if (estimateVehicleKey !== catalogVehicleKey) {
      const params = new URLSearchParams(location.search);
      if (!(location.pathname.includes(ROUTER_ESTIMATE) && params.get(EstimateIdParam))) {
        dispatch(createNewEstimate({ vehicle: catalogVehicleKey !== undefined ? catalogVehicleDetail : undefined }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, catalogVehicleKey, isDelegationLoading]);
}
